import { Controller } from "stimulus"
import { loadComments, saveComment, resolveComment, deleteComment, removeCommentHighlight } from "../tiptap/comments"
import { buildCommentAvatar } from "../tiptap/comment_avatar"

export default class extends Controller {
  static targets = ["list", "count"]
  static values = { postId: String }

  connect() {
    this.comments = []
    this.loadError = null
    // The server-rendered placeholder is the single source of the loading state, so a retry
    // restores exactly the same panel shape.
    this.loadingHtml = this.hasListTarget ? this.listTarget.innerHTML : ""
    this._onRequestCreate = this.handleCreateRequest.bind(this)
    this._onCancelled = this.handleCancelled.bind(this)
    this._onCreated = () => this.load()
    document.addEventListener("comment:request-create", this._onRequestCreate)
    document.addEventListener("comment:cancelled", this._onCancelled)
    document.addEventListener("comment:created", this._onCreated)
    this.load()
  }

  disconnect() {
    document.removeEventListener("comment:request-create", this._onRequestCreate)
    document.removeEventListener("comment:cancelled", this._onCancelled)
    document.removeEventListener("comment:created", this._onCreated)
  }

  handleCancelled(e) {
    const { markerId } = e.detail
    if (!markerId) return
    const editor = window.tiptapEditor
    if (editor) removeCommentHighlight(editor, markerId)
  }

  async load() {
    if (!this.postIdValue) return
    this.loadError = null
    this.showLoadingState()
    try {
      this.comments = await loadComments(this.postIdValue)
    } catch (error) {
      // Unresolved, not empty: never fall back to the empty state after a failed request.
      this.comments = []
      this.loadError = error
    }
    this.render()
  }

  async retry(event) {
    event?.preventDefault()
    await this.load()
  }

  showLoadingState() {
    if (!this.hasListTarget) return
    this.listTarget.setAttribute("aria-busy", "true")
    if (this.loadingHtml) this.listTarget.innerHTML = this.loadingHtml
  }

  handleCreateRequest(e) {
    const { markerId, quoteText } = e.detail
    this.showInlineInput(markerId, quoteText)
  }

  showInlineInput(markerId, quoteText) {
    if (!this.hasListTarget) return

    // Switch to Outline tab if not visible
    const outlineTab = document.querySelector('[data-sidebar-tab-panel-param="sidebarOutlineContent"]')
    if (outlineTab) outlineTab.click()

    const form = document.createElement("div")
    form.className = "comment-input-form mb-3 p-3 border rounded"
    form.innerHTML = `
      <div class="font-size-12 text-muted mb-2">
        <span class="fas fa-quote-left mr-1"></span>
        ${escapeHtml(quoteText.substring(0, 100))}${quoteText.length > 100 ? "..." : ""}
      </div>
      <textarea class="form-control form-control-sm mb-2" rows="2" placeholder="Add a comment..." autofocus></textarea>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm btn-5 comment-submit-btn">Comment</button>
        <button type="button" class="btn btn-sm btn-2 comment-cancel-btn">Cancel</button>
      </div>
    `

    const textarea = form.querySelector("textarea")
    const submitBtn = form.querySelector(".comment-submit-btn")
    const cancelBtn = form.querySelector(".comment-cancel-btn")

    submitBtn.addEventListener("click", async () => {
      const body = textarea.value.trim()
      if (!body) return

      submitBtn.disabled = true
      submitBtn.textContent = "Saving..."

      try {
        const comment = await saveComment({
          postId: this.postIdValue,
          body,
          markerId,
          quoteText,
        })
        this.comments.push(comment)
        form.remove()
        this.render()
      } catch (e) {
        submitBtn.disabled = false
        submitBtn.textContent = "Comment"
      }
    })

    cancelBtn.addEventListener("click", () => {
      form.remove()
      // Remove the highlight mark since comment was cancelled
      document.dispatchEvent(new CustomEvent("comment:cancelled", { detail: { markerId } }))
    })

    this.listTarget.prepend(form)
    setTimeout(() => textarea.focus(), 100)
  }

  async handleResolve(e) {
    const commentId = e.currentTarget.dataset.commentId
    const comment = await resolveComment(this.postIdValue, commentId)
    const idx = this.comments.findIndex(c => c.id === commentId)
    if (idx >= 0) this.comments[idx] = comment
    this.render()
  }

  async handleDelete(e) {
    const commentId = e.currentTarget.dataset.commentId
    if (!confirm("Delete this comment?")) return
    const comment = this.comments.find(c => c.id === commentId)
    await deleteComment(this.postIdValue, commentId)

    // Remove highlight from editor
    if (comment?.marker_id) {
      const editor = window.tiptapEditor
      if (editor) removeCommentHighlight(editor, comment.marker_id)
    }

    this.comments = this.comments.filter(c => c.id !== commentId)
    this.render()
  }

  async handleReply(e) {
    const parentId = e.currentTarget.dataset.commentId
    const parentEntry = e.currentTarget.closest(".comment-entry")
    if (!parentEntry) return

    const markerId = `reply-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    const parent = this.comments.find(c => c.id === parentId)

    const form = document.createElement("div")
    form.className = "comment-reply-form ml-4 mt-2 mb-2"
    form.innerHTML = `
      <textarea class="form-control form-control-sm mb-2" rows="2" placeholder="Reply..." autofocus></textarea>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm btn-5 reply-submit-btn">Reply</button>
        <button type="button" class="btn btn-sm btn-2 reply-cancel-btn">Cancel</button>
      </div>
    `

    const textarea = form.querySelector("textarea")
    const submitBtn = form.querySelector(".reply-submit-btn")
    const cancelBtn = form.querySelector(".reply-cancel-btn")

    submitBtn.addEventListener("click", async () => {
      const body = textarea.value.trim()
      if (!body) return

      submitBtn.disabled = true
      try {
        const reply = await saveComment({
          postId: this.postIdValue,
          body,
          markerId,
          quoteText: parent?.quote_text || "",
          parentId,
        })
        if (parent) {
          parent.replies = parent.replies || []
          parent.replies.push(reply)
        }
        form.remove()
        this.render()
      } catch (e) {
        submitBtn.disabled = false
      }
    })

    cancelBtn.addEventListener("click", () => form.remove())
    parentEntry.appendChild(form)
    setTimeout(() => textarea.focus(), 100)
  }

  render() {
    if (!this.hasListTarget) return
    this.listTarget.setAttribute("aria-busy", "false")

    const openCount = this.comments.filter(c => c.status === "open").length
    if (this.hasCountTarget) {
      this.countTarget.textContent = openCount
      this.countTarget.classList.toggle("d-none", openCount === 0)
    }

    if (this.loadError) {
      const status = Number(String(this.loadError.message || "").match(/(\d{3})/)?.[1]) || 0
      this.listTarget.innerHTML = `
        <div class="sidebar-frame-error" role="status">
          <span class="fal fa-exclamation-triangle sidebar-frame-error-icon" aria-hidden="true"></span>
          <p class="sidebar-frame-error-title">Couldn't load comments.</p>
          <p class="sidebar-frame-error-detail">${status ? `The server responded with ${status}.` : "The request could not be completed."} Check your connection and try again.</p>
          <button type="button" class="btn btn-sm btn-5 sidebar-frame-error-retry" data-action="comment-panel#retry">Retry</button>
        </div>
      `
      return
    }

    if (this.comments.length === 0) {
      this.listTarget.innerHTML = `
        <div class="comment-empty-state text-muted font-size-16 p-4 text-center">
          <span class="fal fa-comment-dots fa-3x opacity-3 d-block mb-3"></span>
          No comments yet. Select text and click the comment button to add one.
        </div>
      `
      return
    }

    this.listTarget.innerHTML = ""
    this.comments.forEach(comment => {
      this.listTarget.appendChild(this.buildCommentEntry(comment))
    })
  }

  buildCommentEntry(comment) {
    const entry = document.createElement("div")
    entry.className = `comment-entry ${comment.status === "resolved" ? "comment-resolved" : ""}`
    entry.dataset.markerId = comment.marker_id

    const isResolved = comment.status === "resolved"

    entry.innerHTML = `
      <div class="comment-header">
        <div class="comment-meta">
          <span class="comment-author">${escapeHtml(comment.profile?.name || "Unknown")}</span>
          <span class="comment-time">${timeAgo(comment.created_at)}</span>
        </div>
      </div>
      ${comment.quote_text ? `<div class="comment-quote">${escapeHtml(comment.quote_text.substring(0, 120))}${comment.quote_text.length > 120 ? "..." : ""}</div>` : ""}
      <div class="comment-body">${escapeHtml(comment.body)}</div>
      <div class="comment-actions">
        <button type="button" class="comment-action-btn reply-btn" data-comment-id="${comment.id}">Reply</button>
        <button type="button" class="comment-action-btn resolve-btn" data-comment-id="${comment.id}">${isResolved ? "Reopen" : "Resolve"}</button>
        <button type="button" class="comment-action-btn delete-btn" data-comment-id="${comment.id}">Delete</button>
      </div>
    `

    entry.querySelector(".comment-header").prepend(buildCommentAvatar(comment.profile))

    // Wire action buttons
    entry.querySelector(".reply-btn")?.addEventListener("click", (e) => this.handleReply(e))
    entry.querySelector(".resolve-btn")?.addEventListener("click", (e) => this.handleResolve(e))
    entry.querySelector(".delete-btn")?.addEventListener("click", (e) => this.handleDelete(e))

    // Render replies
    if (comment.replies && comment.replies.length > 0) {
      const repliesContainer = document.createElement("div")
      repliesContainer.className = "comment-replies"
      comment.replies.forEach(reply => {
        repliesContainer.appendChild(this.buildReplyEntry(reply))
      })
      entry.appendChild(repliesContainer)
    }

    // Click to scroll to highlight
    entry.addEventListener("click", (e) => {
      if (e.target.closest(".comment-action-btn") || e.target.closest(".comment-reply-form")) return
      const mark = document.querySelector(`mark[data-comment-id="${comment.marker_id}"]`)
      if (mark) mark.scrollIntoView({ behavior: "smooth", block: "center" })
    })

    return entry
  }

  buildReplyEntry(reply) {
    const entry = document.createElement("div")
    entry.className = "comment-reply"
    entry.innerHTML = `
      <div class="comment-header">
        <div class="comment-meta">
          <span class="comment-author">${escapeHtml(reply.profile?.name || "Unknown")}</span>
          <span class="comment-time">${timeAgo(reply.created_at)}</span>
        </div>
      </div>
      <div class="comment-body">${escapeHtml(reply.body)}</div>
    `
    entry.querySelector(".comment-header").prepend(buildCommentAvatar(reply.profile, "comment-avatar comment-avatar-sm"))
    return entry
  }
}

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

function timeAgo(dateStr) {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  return date.toLocaleDateString()
}
